import { api } from './api';
import type {
  ArticleDetail,
  ArticleListResponse,
  ArticleUpdate,
  CategoryInfo,
  TagInfo,
} from '@/types/article';

export interface ArticleListParams {
  page?: number;
  per_page?: number;
  categories?: number[];
  after?: string;
  before?: string;
  search?: string;
  status?: string;
  site?: string;
  origem?: string;
}

export interface ArticleOrigem {
  rotulo: string | null;
  fonte: string | null;
  modo: string | null;
  assunto: string | null;
  contexto: { query?: string; editoria?: string; geo?: string } & Record<string, unknown>;
}

export interface EditHistoryItem {
  id: number;
  post_id: number;
  site: string;
  editor_id: string | null;
  changes: Record<string, unknown>;
  edited_at: string;
}

export const articlesApi = {
  list: (params: ArticleListParams = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.per_page) qs.set('per_page', String(params.per_page));
    if (params.categories?.length) qs.set('categories', params.categories.join(','));
    if (params.after) qs.set('after', params.after);
    if (params.before) qs.set('before', params.before);
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    if (params.site) qs.set('site', params.site);
    if (params.origem) qs.set('origem', params.origem);
    return api.get<ArticleListResponse>(`/posts?${qs}`);
  },
  get: (id: number, site?: string) =>
    api.get<ArticleDetail>(`/posts/${id}${site ? `?site=${encodeURIComponent(site)}` : ''}`),
  origem: (id: number, site?: string) =>
    api.get<ArticleOrigem>(
      `/posts/${id}/origem${site ? `?site=${encodeURIComponent(site)}` : ''}`,
    ),
  update: (id: number, payload: ArticleUpdate, site?: string) =>
    api.put<ArticleDetail>(
      `/posts/${id}${site ? `?site=${encodeURIComponent(site)}` : ''}`,
      payload,
    ),
  publishTo: (payload: {
    target_site: string;
    title: string;
    content: string;
    excerpt?: string;
    status?: 'publish' | 'draft';
    featured_media_url?: string;
    categories?: number[];
    category_names?: string[];
  }): Promise<{
    target_site: string;
    wp_id: number;
    wp_link: string;
    status: string;
    image_warning: string | null;
  }> => api.post('/posts/publish-to', payload),
  publications: (
    id: number,
    site?: string,
  ): Promise<{
    publications: {
      site: string;
      url: string;
      post_id: number;
      site_slug?: string;
      current: boolean;
    }[];
  }> =>
    api.get(`/posts/${id}/publications${site ? `?site=${encodeURIComponent(site)}` : ''}`),
  generateTweet: (payload: {
    title: string;
    excerpt?: string;
    content?: string;
    link?: string;
    angle?: string;
  }): Promise<{ tweet: string; length: number; link: string; attempts: number; trimmed: boolean }> =>
    api.post('/posts/tweet', payload),
  history: (id: number) => api.get<EditHistoryItem[]>(`/posts/${id}/history`),
};

export const categoriesApi = {
  list: (site?: string) =>
    api.get<CategoryInfo[]>(`/categories${site ? `?site=${encodeURIComponent(site)}` : ''}`),
};

export const tagsApi = {
  list: (search?: string, site?: string) => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (site) qs.set('site', site);
    const s = qs.toString();
    return api.get<TagInfo[]>(`/tags${s ? `?${s}` : ''}`);
  },
  byIds: (ids: number[], site?: string) => {
    if (ids.length === 0) return Promise.resolve([] as TagInfo[]);
    const qs = new URLSearchParams({
      include: ids.join(','),
      per_page: String(Math.min(ids.length, 100)),
    });
    if (site) qs.set('site', site);
    return api.get<TagInfo[]>(`/tags?${qs}`);
  },
};
